import { mediaModal } from '@/styled-system/recipes';
import { css } from '@/styled-system/css';

export const {
  photoPanel,
  mainImage,
  photoCountBadge,
  thumbnailRail,
  thumbnailButton,
  thumbnailButtonSelected,
  thumbnailImage,
  mediaPanel,
  stage,
  iframe,
  videoPosterButton,
  videoPosterImage,
  videoPosterFallback,
  videoPosterScrim,
  videoPosterOverlay,
  videoPlayIcon,
  mediaItemGrid,
  mediaItemButton,
  tourCardGrid,
  tourCardMedia,
  tourCardImage,
  tourCardFallback,
  tourCardScrim,
  tourCardOverlay,
  tourCardIcon,
  emptyState,
  streetViewCanvas,
  streetViewCanvasHidden,
  streetViewStatus,
  srOnly,
} = mediaModal();

export const content = css.raw({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  background: 'bg.neutral',
  lg: {
    blockSize: '100%',
  },
  // C11n 10.18.1 does not expose styling for the Composite wrapper around Tabs.Root.
  '& [data-c11n-component="Modal.Body"] > div': {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  // Modal.Footer's component recipe owns its inset and padding, so the floating mobile dock needs a scoped override.
  '& [data-c11n-component="Modal.Footer"]': {
    top: 'auto',
    paddingBlock: 'default',
    paddingInlineStart: 'default',
    paddingInlineEnd: 'calc({spacing.default} + var(--floating-ui-scrollbar-width))',
  },
});

export const hiddenModalHeader = css.raw({
  position: 'absolute',
  paddingBlock: 'none',
  paddingInline: 'none',
});

export const mediaHeaderActions = css({
  display: 'flex',
  position: 'absolute',
  top: 'default',
  right: 'default',
  marginBlockStart: 'tightest',
  zIndex: 'zIndexOverParent',
});

export const mediaCloseButton = css.raw({
  position: 'fixed',
  top: 'default',
  right: 'auto',
  left: 'default',
  marginBlockStart: 'tightest',
  zIndex: 'zIndexOverParent',
  lg: {
    position: 'absolute',
    right: 'auto',
  },
});

export const mediaCloseControl = css.raw({
  position: 'relative',
  insetBlockStart: 'auto',
  insetInlineEnd: 'auto',
  zIndex: 'zIndexStep',
});

export const mediaBody = css.raw({
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  minHeight: 0,
  overflowY: 'hidden',
  paddingBlock: 'none',
  paddingInline: 'none',
});

export const mediaDesktopTabsBar = css({
  display: 'flex',
  position: 'absolute',
  top: 'default',
  left: '50%',
  marginBlockStart: 'tightest',
  width: 'auto',
  maxWidth: 'calc(100% - {spacing.layout.vast.loose} * 4)',
  transform: 'translateX(-50%)',
  zIndex: 'zIndexOverParent',
  pointerEvents: 'auto',
});

export const mediaMobileTabsBar = css({
  width: '100%',
  pointerEvents: 'auto',
  '& > [data-c11n-component="ToggleButtonGroup"]': {
    width: '100%',
  },
});

export const mediaCategoryToggleButton = css.raw({
  minWidth: 0,
  paddingInline: 'default',
  whiteSpace: 'nowrap',
});

export const mediaPanelViewport = css.raw({
  position: 'relative',
  // Contains third-party stacking (Google Street View, tour and video iframes) so modal chrome stays on top.
  isolation: 'isolate',
  display: 'flex',
  flex: '1 1 auto',
  minHeight: 0,
  overflow: 'hidden',
});

export const mediaFooter = css.raw({
  position: 'absolute',
  top: 'auto',
  right: 0,
  bottom: 0,
  left: 0,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'tighter',
  paddingBlock: 'default',
  paddingInlineStart: 'default',
  paddingInlineEnd: 'calc({spacing.default} + var(--floating-ui-scrollbar-width))',
  borderTopWidth: 'default',
  borderTopStyle: 'solid',
  borderTopColor: 'border.neutral',
  background: 'transparent',
  minHeight: '1800',
  zIndex: 'zIndexOverParent',
  lg: {
    display: 'none',
  },
});

export const mediaFooterGlass = css({
  boxShadow: 'sm',
  inset: 0,
  pointerEvents: 'none',
  position: 'absolute',
  zIndex: 'zIndexBase',
});

export const mediaDesktopFooter = css({
  position: 'absolute',
  bottom: 'default',
  left: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 'md',
  padding: 'tighter',
  transform: 'translateX(-50%)',
  zIndex: 'zIndexOverParent',
  pointerEvents: 'auto',
});

export const mediaDesktopFooterGlass = css({
  position: 'absolute',
  inset: 0,
  borderRadius: 'md',
  boxShadow: 'smHeavy',
  pointerEvents: 'none',
  zIndex: 'zIndexBase',
  _before: {
    borderRadius: 'md',
  },
});

export const mediaActionGroup = css.raw({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'nowrap',
  gap: 'tighter',
  justifyContent: 'flex-start',
  position: 'relative',
  width: '100%',
  pointerEvents: 'auto',
  zIndex: 'zIndexStep',
  '& > [data-media-primary-action]': {
    flexGrow: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
  },
  lg: {
    width: 'auto',
  },
});

export const mediaVerticalActionGroup = css.raw({
  position: 'relative',
  width: '100%',
  pointerEvents: 'auto',
  zIndex: 'zIndexStep',
});

export const mediaActionSecondaryOnImpact = css({
  _hover: {
    boxShadowColor: 'border.action.neutral-fixed.hover',
  },
  _active: {
    boxShadowColor: 'border.action.neutral-fixed.pressed',
  },
});

export const mediaMobileHeaderActions = css({
  position: 'fixed',
  top: 'default',
  right: 'default',
  zIndex: 'zIndexOverParent',
  pointerEvents: 'auto',
  lg: {
    display: 'none',
  },
});

export const mobileFooterClearance = css({
  paddingBlockEnd: 'mediaModal.mobileDockClearance',
  scrollPaddingBlockEnd: 'mediaModal.mobileDockClearance',
});

export const mobileControlsClearance = css({
  paddingBlockEnd: 'mediaModal.mobileControlsClearance',
  scrollPaddingBlockEnd: 'mediaModal.mobileControlsClearance',
});

export const desktopFooterClearance = css({
  lg: {
    paddingBlockEnd: 'mediaModal.desktopFooterClearance',
    scrollPaddingBlockEnd: 'mediaModal.desktopFooterClearance',
  },
});

export const desktopEdgeClearance = css({
  lg: {
    paddingBlockEnd: 'default',
    scrollPaddingBlockEnd: 'default',
  },
});

export const photoGalleryScroller = css.raw({
  position: 'absolute',
  // Panda does not emit a valid inset utility for semantic `none`; generated `zero` is required here.
  inset: 'zero',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  paddingBlockStart: 'zero',
  paddingBlockEnd: 'mediaModal.mobileDockClearance',
  paddingInline: 'zero',
  scrollPaddingBlockEnd: 'mediaModal.mobileDockClearance',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  lg: {
    paddingBlock: 'zero',
    paddingInline: 'zero',
    scrollBehavior: 'smooth',
    scrollPaddingBlockEnd: 'zero',
  },
  '@media (prefers-reduced-motion: reduce)': {
    scrollBehavior: 'auto',
  },
});

export const mediaEmptyState = css.raw({
  display: 'flex',
  minHeight: 'obj.xxl',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 'md',
  background: 'bg.softest',
  padding: 'loose',
  textAlign: 'center',
});

export const photoGalleryGrid = css.raw({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gap: 'tighter',
  width: '100%',
  // Product-specific media gallery width has no semantic C11n token.
  // oxlint-disable-next-line constellation/no-raw-spacing-values -- `maxWidth: [1280px]` is past the end of the numeric sizes scale, which stops at `5000` (200px)
  maxWidth: '[1280px]',
  marginInline: 'auto',
  lg: {
    gridTemplateColumns: 'repeat(10, minmax(0, 1fr))',
    // Product-specific media row height has no semantic C11n token.
    gridAutoRows: '[400px]',
  },
});

export const photoGalleryCellWide = css.raw({
  display: 'block',
  width: '100%',
  aspectRatio: '4 / 3',
  overflow: 'hidden',
  background: 'bg.softest',
  cursor: 'pointer',
  _focusVisible: {
    boxShadow: 'focus',
  },
  lg: {
    height: '100%',
    aspectRatio: 'auto',
    gridColumn: 'span 6',
  },
});

export const photoGalleryCellNarrow = css.raw({
  display: 'block',
  width: '100%',
  aspectRatio: '4 / 3',
  overflow: 'hidden',
  background: 'bg.softest',
  cursor: 'pointer',
  _focusVisible: {
    boxShadow: 'focus',
  },
  lg: {
    height: '100%',
    aspectRatio: 'auto',
    gridColumn: 'span 4',
  },
});

export const photoGalleryFallbackCellWide = css.raw({
  display: 'block',
  width: '100%',
  aspectRatio: '4 / 3',
  overflow: 'hidden',
  background: 'bg.softest',
  cursor: 'default',
  lg: {
    height: '100%',
    aspectRatio: 'auto',
    gridColumn: 'span 6',
  },
});

export const photoGalleryFallbackCellNarrow = css.raw({
  display: 'block',
  width: '100%',
  aspectRatio: '4 / 3',
  overflow: 'hidden',
  background: 'bg.softest',
  cursor: 'default',
  lg: {
    height: '100%',
    aspectRatio: 'auto',
    gridColumn: 'span 4',
  },
});

export const photoGalleryImage = css({
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  pointerEvents: 'none',
});

export const lightboxContent = css.raw({
  display: 'flex',
  flexDirection: 'column',
  height: '100dvh',
  maxHeight: '100dvh',
  overflow: 'hidden',
  background: 'bg.impact-fixed',
});

export const lightboxBody = css.raw({
  display: 'flex',
  flex: '1 1 auto',
  minHeight: 0,
  overflow: 'hidden',
  paddingBlock: 'none',
  paddingInline: 'none',
});

export const lightboxStage = css.raw({
  position: 'relative',
  display: 'flex',
  flex: '1 1 auto',
  width: '100%',
  minHeight: 0,
  background: 'bg.impact-fixed',
  // The lightbox owns pinch zoom for the photo, so the browser must not zoom or scroll the page.
  touchAction: 'none',
});

export const lightboxCarousel = css.raw({
  borderRadius: 'none',
  background: 'bg.impact-fixed',
  '& > .c11n_photo-carousel__nav-controls': {
    opacity: 1,
    zIndex: 'zIndexOverParent',
    // Swipe is the mobile control; the buttons stay for screen readers and keyboard focus.
    lgDown: {
      '&:not(:focus-within)': {
        srOnly: true,
      },
    },
  },
});

export const lightboxPhoto = css.raw({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  transformOrigin: 'center',
  willChange: 'transform',
});

export const lightboxImage = css({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
});

export const lightboxPreviousButton = css.raw({
  position: 'absolute',
  insetBlock: 'zero',
  marginBlock: 'auto',
  '&&': {
    height: 'action.sm',
    color: 'icon.neutral',
  },
  '&&&:active, &&&:hover': {
    color: 'icon.neutral',
  },
  insetInlineStart: 'default',
});

export const lightboxNextButton = css.raw({
  position: 'absolute',
  insetBlock: 'zero',
  marginBlock: 'auto',
  '&&': {
    height: 'action.sm',
    color: 'icon.neutral',
  },
  '&&&:active, &&&:hover': {
    color: 'icon.neutral',
  },
  insetInlineEnd: 'default',
});

export const lightboxFooter = css.raw({
  position: 'absolute',
  inset: 'zero',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-end',
  zIndex: 'zIndexOverParent',
  background: 'transparent',
  paddingBlockStart: 'default',
  paddingInlineEnd: 'default',
  pointerEvents: 'none',
});

export const lightboxPhotoCountSurface = css({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  minHeight: 'action.sm',
  marginBlockStart: 'tightest',
  borderRadius: 'pill',
  paddingInline: 'tighter',
  paddingBlock: 'tightest',
});

export const lightboxPhotoCountGlass = css({
  position: 'absolute',
  inset: 0,
  borderRadius: 'pill',
  boxShadow: 'sm',
  pointerEvents: 'none',
  zIndex: 'zIndexBase',
  _before: {
    borderRadius: 'pill',
  },
});

export const lightboxPhotoCount = css.raw({
  position: 'relative',
  color: 'text.neutral',
  zIndex: 'zIndexStep',
});

export const mainImageStage = css.raw({
  minHeight: 0,
  borderRadius: 'md',
  // oxlint-disable-next-line constellation/no-raw-color-values
  background: 'black',
});

export const tourCardButton = css({
  display: 'block',
  overflow: 'hidden',
  background: 'bg.softest',
  // oxlint-disable-next-line constellation/no-raw-color-values
  color: 'white',
  cursor: 'pointer',
  padding: 'zero',
  textAlign: 'center',
  _focusVisible: {
    boxShadow: 'focus',
  },
});

export const tourCardLabel = css({
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  // oxlint-disable-next-line constellation/no-raw-color-values
  color: 'white',
});

export const photoGalleryWithoutFooter = css.raw({
  paddingBlockEnd: 'mediaModal.mobileControlsClearance',
  scrollPaddingBlockEnd: 'mediaModal.mobileControlsClearance',
  lg: { paddingBlockEnd: 'zero', scrollPaddingBlockEnd: 'zero' },
});
